import { Component, h, Prop, Watch, Element, Host, State } from '@stencil/core';
import { getLang } from '@union-investment/global-resources/src/utilities/language';
import { isMagnoliaEditingMode } from '../../../helper';

interface QuickFactData {
  icon: string;
  headline: string;
  text: string;
}

interface PropertyInfoResponse {
  title: string;
  latitude: string;
  longitude: string;
  adress: string;
  adresszeile: string;
  fond: string;
  immobilientyp: string;
  objektbeschreibung: string;
  ausstattung: string;
  baujahr: string;
  umbaujahr: string;
  erwerbsjahr: string;
  nutzfläche: string;
}

interface UiCmsRealEstateOverviewBody {
  searchApiKey: string;
  searchApiUrl: string;
  searchApiVersion: string;
  businessUnitNumber: string;
  showFonds: boolean; // apiData.fond
  showYearOfModification: boolean; // apiData.umbaujahr
  showFurniture: boolean; // apiData.ausstattung
  showYearOfConstruction: boolean; // apiData.baujahr
  showYearOfPurchase: boolean; // apiData.erwerbsjahr
  showUsefulAreaOfTheProperty: boolean; // apiData.nutzfläche
  showTypeOfRealEstate: boolean; // apiData.immobilientyp
}

@Component({
  tag: 'ui-cms-real-estate-overview',
  styleUrl: 'ui-cms-real-estate-overview.scss',
  shadow: true,
})
export class UiCmsRealEstateOverview {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsRealEstateOverviewBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop({ mutable: true }) i18n = {
    fonds: 'Fonds',
    yearOfModification: 'umgebaut',
    furniture: 'Ausstattung',
    yearOfConstruction: 'fertiggestellt',
    yearOfPurchase: 'erworben',
    usefulAreaOfTheProperty: 'Nutzfläche',
    typeOfRealEstate: 'Art der Immobilie',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  // loading state to condition display on API call
  @State() isFetchingResults = false;

  // property info fetched from passed in API URL
  @State() propertyInfo: PropertyInfoResponse = {} as any;

  // property info formatted as QuickFactData for easy display
  @State() quickFactData: QuickFactData[] = [];

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  /**
    The API URL passed in utilityBody is called on componentWillLoad() and its response
    is set as the propertyInfo state.  This watcher then picks up that data, formats it
    as QuickFactData, and sets it as the quickFactData state so it can be displayed.
  */
  @Watch('propertyInfo')
  propertyInfoChangeHandler() {
    this.quickFactData = this.formatQuickFactData();
  }

  /**
    Formats propertyInfo returned by API into QuickFactData that can be passed to individual
    ui-quick-fact components to display.
  */
  private formatQuickFactData(): QuickFactData[] {
    const propertyMap = {
      showFonds: { icon: 'money-bag', headline: this.i18n.fonds, text: this.propertyInfo.fond },
      showTypeOfRealEstate: {
        icon: 'skyscraper',
        headline: this.propertyInfo.immobilientyp,
        text: this.propertyInfo.objektbeschreibung,
      },
      showFurniture: { icon: 'armchair', headline: this.i18n.furniture, text: this.propertyInfo.ausstattung },
      showYearOfConstruction: {
        icon: 'bricks',
        headline: this.propertyInfo.baujahr,
        text: this.i18n.yearOfConstruction,
      },
      showYearOfModification: {
        icon: 'bricks',
        headline: this.propertyInfo.umbaujahr,
        text: this.i18n.yearOfModification,
      },
      showYearOfPurchase: { icon: 'calendar', headline: this.propertyInfo.erwerbsjahr, text: this.i18n.yearOfPurchase },
      showUsefulAreaOfTheProperty: {
        icon: 'plot-useable-area',
        headline: this.propertyInfo.nutzfläche,
        text: this.i18n.usefulAreaOfTheProperty,
      },
    };

    return Object.entries(propertyMap).reduce((acc, [key, value]) => {
      if (this.body[key]) {
        acc.push(value);
      }
      return acc;
    }, []);
  }

  /**
    Takes the api info passed in utilityBody and returns it with all necessary query params.
    * @param apiUrl received from BE in body prop
    * @param apiVersion received from BE in body prop
    * @param businessUnitNumber received from BE in body prop
  */
  private getUrlWithParams(apiUrl, apiVersion, businessUnitNumber): string {
    const urlSearchParams = new URLSearchParams({
      action: 'getLiegenschaftsInfo',
      locale: getLang(),
      'api-version': apiVersion,
      'liegenschaft-id': businessUnitNumber,
    });

    const url = new URL(apiUrl);
    url.search = urlSearchParams.toString();

    return url.toString();
  }

  /**
    Calls GET method on passed in API URL and sets the result as the propertyInfo state.
    * @param url with query params
    * @param apiKey for API call
  */
  private async fetchPropertyData(url, apiKey) {
    this.isFetchingResults = true;

    try {
      const response = await fetch(url, {
        headers: {
          'x-api-key': apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const propertyInfo: PropertyInfoResponse = await response.json();

      this.propertyInfo = propertyInfo;
    } catch (error) {
      // TODO: REVIEWER: error handling concept?
      console.error(error);
    }

    this.isFetchingResults = false;
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
    // fetch property data on load
    const { searchApiUrl, searchApiVersion, businessUnitNumber, searchApiKey } = this.body;
    const url = this.getUrlWithParams(searchApiUrl, searchApiVersion, businessUnitNumber);
    this.fetchPropertyData(url, searchApiKey);
  }

  render() {
    const { title, adresszeile } = this.propertyInfo;

    return (
      <Host>
        {this.isFetchingResults ? (
          <div class="spinner">
            <sd-spinner style={{ fontSize: '2rem' }} color="primary"></sd-spinner>
          </div>
        ) : (
          <div>
            <ui-headline isCentered={true} level={2} innerHTML={title}></ui-headline>
            <div class="address-container">
              <ui-icon icon="location" variant="colored"></ui-icon>
              <address innerHTML={adresszeile}></address>
            </div>
            <ui-quickfact-group iconSize="small" iconLayout="above">
              {this.quickFactData.map((quickFact) => (
                <ui-quickfact
                  class={{ 'magnolia-editing-mode': isMagnoliaEditingMode() }}
                  headline={quickFact.headline}
                  text={quickFact.text}
                >
                  <ui-icon slot="icon" icon={quickFact.icon} type="content"></ui-icon>
                </ui-quickfact>
              ))}
            </ui-quickfact-group>
          </div>
        )}
      </Host>
    );
  }
}
